import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Link,
  Separator,
  SimpleGrid,
  Stack,
  Heading,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMail } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { buildRegistrationUrl } from '../../utils/billingCheckout';
import { getAppVersion } from '../../utils/version';

/**
 * Public footer component used on all public-facing pages.
 * Provides consistent marketing, resource, legal, and copyright links.
 */
export const PublicFooter: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  return (
    <Box as="footer" bg="gray.800" color="white" py={12} mt="auto">
      <Container maxW="7xl">
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={8} mb={8}>
          <VStack align={{ base: 'center', lg: 'start' }} gap={4}>
            <Heading size="lg" color="brand.400">
              LiveWello
            </Heading>
            <Text fontSize="sm" color="gray.400" maxW="300px" textAlign={{ base: 'center', lg: 'left' }}>
              {t('home.footer_tagline')}
            </Text>
            <HStack gap={4} mt={2}>
              <Link href="mailto:support@livewello.com" color="gray.400" _hover={{ color: 'brand.400' }} aria-label="Email">
                <FiMail size="20px" color="currentColor" />
              </Link>
            </HStack>
          </VStack>

          <VStack align={{ base: 'center', lg: 'start' }} gap={3}>
            <Heading size="sm" color="white" mb={1}>
              Product
            </Heading>
            <VStack align={{ base: 'center', lg: 'start' }} gap={2}>
              <Link href="/#features" fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                {t('home.nav.features')}
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/how-to-use">{t('home.nav.how_it_works')}</RouterLink>
              </Link>
              <Link href="/#pricing" fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                {t('home.nav.pricing')}
              </Link>
              <Link href="/#faq" fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                {t('home.nav.faq')}
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/dna-kit">{t('home.nav.dna_kit')}</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/goals-partners">Goals Partners</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/coaching">Health Coaching Program</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/affiliates">Affiliate Program</RouterLink>
              </Link>
            </VStack>
          </VStack>

          <VStack align={{ base: 'center', lg: 'start' }} gap={3}>
            <Heading size="sm" color="white" mb={1}>
              Resources
            </Heading>
            <VStack align={{ base: 'center', lg: 'start' }} gap={2}>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/scenarios">Case Studies</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/tutorials">Product Tutorials</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/presentations">Presentations</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/press">Press</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to={currentUser ? '/chat' : buildRegistrationUrl({ source: 'public-footer-health-chat' })}>
                  Try the health chat
                </RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/about">{t('home.footer_about')}</RouterLink>
              </Link>
              <Link href="mailto:support@livewello.com" fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                {t('home.footer_contact')}
              </Link>
            </VStack>
          </VStack>

          <VStack align={{ base: 'center', lg: 'start' }} gap={3}>
            <Heading size="sm" color="white" mb={1}>
              Legal
            </Heading>
            <VStack align={{ base: 'center', lg: 'start' }} gap={2}>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/terms">{t('home.footer_terms')}</RouterLink>
              </Link>
              <Link asChild fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                <RouterLink to="/privacy">{t('home.footer_privacy')}</RouterLink>
              </Link>
              <Link href="/privacy#cookies-tracking" fontSize="sm" color="gray.300" _hover={{ color: 'white' }}>
                Cookie Policy
              </Link>
            </VStack>
          </VStack>
        </SimpleGrid>

        <Separator my={8} />

        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align="center" gap={2}>
          <Text fontSize="xs" color="gray.500" textAlign="center">
            {t('home.footer_copyright')}
          </Text>
          <Text fontSize="xs" color="gray.600" fontFamily="mono" textAlign="center">
            v{getAppVersion()}
          </Text>
        </Stack>
      </Container>
    </Box>
  );
};

export default PublicFooter;
